import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Star, Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errMsg } from "../../lib/api";

const input = "mt-1.5 h-11 w-full border border-white/15 bg-black/40 px-3 text-sm text-white focus:border-[#a259ff] focus:outline-none";
const lbl = "block font-mono text-[11px] uppercase tracking-widest text-slate-400";
const EMPTY = { name: "", detail: "", text: "", rating: 5 };

export const ReviewsTab = () => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = () => api.get("/admin/reviews").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/settings").then((r) => setSettings(r.data ?? null)).catch(() => setSettings(null));
  }, []);

  const toggleAuto = async (on) => {
    const next = { ...settings, reviews_auto_publish: on };
    try {
      await api.put("/admin/settings", next);
      setSettings(next);
      toast.success(on ? "Customer reviews now go live instantly" : "Customer reviews now wait for your approval");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const approve = async (r) => {
    try {
      await api.post(`/admin/reviews/${r.id}/approve`);
      toast.success("Review is live");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const reset = () => { setF(EMPTY); setEditId(null); };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, rating: Number(f.rating) };
    try {
      if (editId) await api.put(`/admin/reviews/${editId}`, body);
      else await api.post("/admin/reviews", body);
      toast.success(editId ? "Review updated" : "Review added");
      reset();
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const edit = (r) => { setEditId(r.id); setF({ name: r.name, detail: r.detail ?? "", text: r.text, rating: r.rating ?? 5 }); };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from ${r.name}?`)) return;
    await api.delete(`/admin/reviews/${r.id}`).catch((err) => toast.error(errMsg(err)));
    if (editId === r.id) reset();
    load();
  };

  return (
    <div className="mt-6 grid gap-8 lg:grid-cols-12">
      <form onSubmit={save} className="space-y-4 border border-white/10 bg-[#0f0f16] p-5 lg:col-span-4" data-testid="admin-review-form">
        <p className="font-display text-3xl uppercase">{editId ? "Edit review" : "New review"}</p>
        <label className={lbl}>Customer name<input required maxLength={60} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="review-name-input" /></label>
        <label className={lbl}>What they bought (optional)<input maxLength={60} value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} placeholder="Los Santos Legend" className={input} data-testid="review-detail-input" /></label>
        <label className={lbl}>Stars
          <select value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} className={input} data-testid="review-rating-select">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
          </select>
        </label>
        <label className={lbl}>Review<textarea required maxLength={600} rows={4} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={`${input} h-auto py-2`} data-testid="review-text-input" /></label>
        <button className="h-12 w-full bg-[#ff3dc8] font-mono text-xs font-bold uppercase tracking-widest text-white" data-testid="review-save-button">{editId ? "Save changes" : "Add review"}</button>
        {editId && <button type="button" onClick={reset} className="w-full font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-white" data-testid="review-cancel-edit">Cancel edit</button>}
      </form>
      <div className="space-y-3 lg:col-span-8">
        {settings && (
          <label className="flex items-center justify-between gap-4 border border-white/10 bg-[#0f0f16] p-4" data-testid="reviews-auto-publish">
            <span>
              <span className="block font-mono text-xs uppercase tracking-widest text-white">Publish customer reviews instantly</span>
              <span className="mt-1 block text-sm text-slate-400">Off: reviews from the follow-up email wait here for your approval.</span>
            </span>
            <Switch checked={!!settings.reviews_auto_publish} onCheckedChange={toggleAuto} data-testid="reviews-auto-publish-switch" />
          </label>
        )}
        {!items.length && <p className="text-slate-400" data-testid="admin-reviews-empty">No reviews yet. Added reviews show on the homepage.</p>}
        {items.map((r) => (
          <div key={r.id} className="flex items-start gap-4 border border-white/10 bg-[#0f0f16] p-4" data-testid={`admin-review-row-${r.id}`}>
            <div className="flex-1">
              <p className="flex flex-wrap items-center gap-1 font-mono text-xs text-[#e9d5ff]">{r.rating}<Star className="h-3 w-3 fill-[#e9d5ff]" /> · {r.name}{r.detail ? ` · ${r.detail}` : ""}
                {r.status === "pending" && <span className="ml-2 bg-[#ff3dc8] px-2 py-0.5 text-[11px] uppercase tracking-widest text-white" data-testid={`review-pending-${r.id}`}>Waiting approval</span>}
                {r.order_id && r.status !== "pending" && <span className="ml-2 text-[11px] uppercase tracking-widest text-slate-400">Verified buyer</span>}
              </p>
              <p className="mt-2 text-sm text-slate-200">{r.text}</p>
            </div>
            {r.status === "pending" && <button onClick={() => approve(r)} className="p-2 text-slate-300 hover:text-[#a259ff]" aria-label="Approve review" data-testid={`review-approve-${r.id}`}><Check className="h-4 w-4" /></button>}
            <button onClick={() => edit(r)} className="p-2 text-slate-300 hover:text-white" aria-label="Edit review" data-testid={`review-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(r)} className="p-2 text-slate-300 hover:text-[#ff3dc8]" aria-label="Delete review" data-testid={`review-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
